import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SITIO } from './imagenCompartir';
import './Compartir.css';

/**
 * Compartir un extracto o una jugada — la plomería.
 *
 * Lo que se comparte es una IMAGEN (ver `imagenCompartir.js`): un texto se
 * edita antes de reenviarlo y cambiarle un número a un extracto oficial es
 * trivial.
 *
 * ── Dos experiencias, porque son dos aparatos distintos ──────────────────
 *
 *  TELÉFONO (`pointer: coarse`) — `navigator.share({ files })`. Abre la hoja
 *  del sistema y ahí la imagen VIAJA: WhatsApp la recibe como adjunto. Es el
 *  camino de la mayoría del tráfico.
 *   · `imagen-nativa` → va la IMAGEN SOLA, sin texto al lado. La imagen ya
 *     lleva todo lo que hace verificable el dato, y el texto era justo la
 *     parte editable, además de ocupar la vista previa del chat. En un
 *     extracto va también el link a ese extracto: es corto, viaja entero y
 *     deja comprobarlo en el sitio.
 *   · `texto-nativo`  → hay `share` pero no acepta archivos. Recién ahí
 *     aparece el texto, que es lo único que queda.
 *
 *  ESCRITORIO — acá NO se nombra a WhatsApp. `wa.me` es un link de texto por
 *  diseño: no adjunta archivos, nunca. La versión anterior ponía el logo de
 *  WhatsApp, copiaba, descargaba y abría una pestaña con el texto, y le
 *  dejaba a la persona el trabajo de adjuntar. Prometía un envío que no
 *  ocurría. Lo que queda son dos acciones que sí se cumplen enteras:
 *
 *   1. **Copiar la imagen** — un clic, `ClipboardItem` con el PNG, listo.
 *      La descarga aparece SOLO si el portapapeles falla: es la
 *      consecuencia de un error, no un paso del camino feliz.
 *   2. **Copiar el link** del extracto — un link de texto sí llega a
 *      cualquier chat, y abre ese mismo extracto del otro lado.
 *
 *  Y si el navegador no sabe copiar imágenes (`ClipboardItem` ausente), el
 *  botón dice "Descargar la imagen" desde el principio. El rótulo nunca
 *  promete algo que ese navegador no va a hacer.
 *
 * S7 · Acá no hay ningún control de seguridad. Todo esto es UX.
 */

/** ¿El navegador sabe compartir archivos? Se responde sin `await`, a
 *  propósito: la decisión tiene que tomarse dentro del gesto del usuario. */
export function aceptaArchivos() {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return false;
  try {
    const sonda = new File([new Uint8Array(1)], 'sonda.png', { type: 'image/png' });
    return navigator.canShare({ files: [sonda] });
  } catch (e) {
    return false;
  }
}

function esTactil() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/** 'imagen-nativa' · 'texto-nativo' · 'escritorio'
 *
 *  El camino nativo se reserva a punteros gruesos. Chrome en Windows TAMBIÉN
 *  expone `navigator.share` y `canShare({files})`, pero lo que abre es la
 *  hoja de Windows, donde WhatsApp aparece solo si la persona tiene WhatsApp
 *  Desktop instalado. Con el mouse, copiar es una acción que se cumple
 *  siempre; la hoja del sistema, no. */
export function rutaDeCompartir() {
  if (!esTactil()) return 'escritorio';
  if (aceptaArchivos()) return 'imagen-nativa';
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') return 'texto-nativo';
  return 'escritorio';
}

/** ¿Sabe poner un PNG en el portapapeles? Decide el RÓTULO del botón, así que
 *  se resuelve antes de pintar y no después del clic. */
export function copiaImagenes() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (!navigator.clipboard || typeof navigator.clipboard.write !== 'function') return false;
  return typeof window.ClipboardItem === 'function';
}

/** Solo para el respaldo del teléfono: si `navigator.share` falla, WhatsApp
 *  con el texto sigue siendo mejor que nada. En escritorio no se usa —ahí el
 *  link de texto es justamente lo que se está corrigiendo—. */
export function enlaceWhatsApp(texto, url) {
  const cuerpo = url ? `${texto}\n${url}` : texto;
  return `https://wa.me/?text=${encodeURIComponent(cuerpo)}`;
}

function descargar(archivo) {
  const href = URL.createObjectURL(archivo);
  const a = document.createElement('a');
  a.href = href;
  a.download = archivo.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 20000);
}

/**
 * Copia el PNG. Devuelve 'ok' · 'sin-imagen' · 'sin-portapapeles'.
 *
 * Recibe la PROMESA y no el archivo: `ClipboardItem` acepta un
 * `Promise<Blob>`, y así `clipboard.write` se llama en el mismo tick del
 * clic. Con un `await` antes, el navegador ya perdió el gesto del usuario y
 * rechaza la escritura ("NotAllowedError") aunque el permiso esté dado.
 */
async function copiarImagen(promesaArchivo) {
  const png = promesaArchivo.then((archivo) => {
    if (!archivo) throw new Error('sin-imagen');
    return archivo;
  });
  png.catch(() => {}); // que no quede como rechazo sin atender si falla antes

  try {
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': png })]);
    return 'ok';
  } catch (e) {
    // Distinguir las dos causas: no es lo mismo "el dibujo falló" que "el
    // navegador no me deja escribir el portapapeles".
    const archivo = await promesaArchivo.catch(() => null);
    return archivo ? 'sin-portapapeles' : 'sin-imagen';
  }
}

async function copiarTexto(txt) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(txt);
      return true;
    }
  } catch (e) {
    /* sigue por el respaldo */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = txt;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch (e) {
    return false;
  }
}

/* ═════════════════════════════ Los íconos ═════════════════════════════ */

const trazo = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };

function Svg({ children }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g {...trazo}>{children}</g>
    </svg>
  );
}

function IconoCompartir() {
  return (
    <Svg>
      <circle cx="18" cy="5" r="2.6" />
      <circle cx="6" cy="12" r="2.6" />
      <circle cx="18" cy="19" r="2.6" />
      <path d="M8.4 10.8 15.6 6.6M8.4 13.2l7.2 4.2" />
    </Svg>
  );
}

/** Dos hojas: el gesto universal de copiar. La de atrás lleva el sol y el
 *  horizonte de una imagen, para que se lea "copiar la imagen" sin el rótulo. */
function IconoCopiarImagen() {
  return (
    <Svg>
      <rect x="8.6" y="3.4" width="12" height="12" rx="2.4" />
      <circle cx="12.4" cy="7.4" r="1.1" />
      <path d="M9.1 13.2l3.3-3.1 2.6 2.4 2.2-1.9 3.2 2.9" />
      <path d="M15.4 18.2v1.9a2.4 2.4 0 0 1-2.4 2.4H5.8a2.4 2.4 0 0 1-2.4-2.4v-7.2a2.4 2.4 0 0 1 2.4-2.4h1.8" />
    </Svg>
  );
}

function IconoWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
      <path d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.2-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5 4.5.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z" />
      <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.4A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-2.9.8.8-2.8-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
    </svg>
  );
}

function IconoLink() {
  return (
    <Svg>
      <path d="M10.2 13.8a3.9 3.9 0 0 0 5.9.4l2.6-2.6a3.9 3.9 0 0 0-5.5-5.5l-1.5 1.5" />
      <path d="M13.8 10.2a3.9 3.9 0 0 0-5.9-.4l-2.6 2.6a3.9 3.9 0 0 0 5.5 5.5l1.5-1.5" />
    </Svg>
  );
}

function IconoDescargar() {
  return (
    <Svg>
      <path d="M12 3.6v10.8" />
      <path d="m7.8 10.4 4.2 4.2 4.2-4.2" />
      <path d="M4.4 16.6v1.8a2.2 2.2 0 0 0 2.2 2.2h10.8a2.2 2.2 0 0 0 2.2-2.2v-1.8" />
    </Svg>
  );
}

function IconoHecho() {
  return (
    <Svg>
      <path d="m4.8 12.6 4.6 4.6 9.8-10.4" />
    </Svg>
  );
}

/* ══════════════════════════════ El botón ══════════════════════════════ */

const MS_CONFIRMACION = 6000;

/**
 * @param {() => Promise<File|null>} hacerArchivo  arma la imagen
 * @param {string} firma      cambia cuando cambia el contenido: invalida la caché
 * @param {string} texto      el respaldo en texto, sin el link (se agrega solo)
 * @param {string} titulo     título para la hoja nativa
 * @param {string} aria       qué comparte, dicho completo para el lector de pantalla
 * @param {string} [ariaCopia]   ídem, para el botón de copiar la imagen (escritorio)
 * @param {string} [enlace]      link a este extracto puntual; sin él no se dibuja el botón
 * @param {string} [resumen]     una línea corta para acompañar el link en WhatsApp.
 *                               NO los 20 números: el dato completo va del otro
 *                               lado del link, donde no se puede editar.
 * @param {string} [ariaEnlace]  qué abre ese link, dicho completo
 * @param {'solido'|'noche'} variante
 */
export default function BotonCompartir({
  hacerArchivo,
  firma,
  texto,
  titulo,
  aria,
  ariaCopia,
  enlace,
  resumen,
  ariaEnlace,
  url = SITIO,
  variante = 'solido',
  tabIndex,
  etiqueta,
}) {
  const [ruta] = useState(rutaDeCompartir);
  const [puedeCopiar] = useState(copiaImagenes);
  const [logro, setLogro] = useState(null); // 'imagen-copiada' | 'imagen-descargada' | 'link-copiado'
  const [estado, setEstado] = useState('');
  const [tono, setTono] = useState('');     // '' | 'ok' | 'error'
  const [ofrecerDescarga, setOfrecerDescarga] = useState(false);
  const [linkALaVista, setLinkALaVista] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const cache = useRef({ firma: null, promesa: null });
  const reloj = useRef(null);
  const campoLink = useRef(null);

  useEffect(() => () => clearTimeout(reloj.current), []);

  /* Se empieza a dibujar la imagen en `pointerdown`, antes del `click`.
     No es capricho: iOS exige que `navigator.share` salga de un gesto vivo, y
     un `await` largo entre el toque y la llamada lo apaga. En escritorio pasa
     lo mismo con el portapapeles. Con la imagen ya lista, el click llama a
     compartir o a copiar casi en seco. */
  const preparar = useCallback(() => {
    if (cache.current.firma === firma && cache.current.promesa) return cache.current.promesa;
    const promesa = Promise.resolve()
      .then(hacerArchivo)
      .catch(() => null);
    cache.current = { firma, promesa };
    return promesa;
  }, [firma, hacerArchivo]);

  const limpiar = useCallback(() => {
    clearTimeout(reloj.current);
    setLogro(null);
    setEstado('');
    setTono('');
    setOfrecerDescarga(false);
    setLinkALaVista(false);
  }, []);

  /* La confirmación se va sola: si se quedara fija, un segundo clic no se
     distinguiría del primero. El error NO se va solo — se va cuando la
     persona vuelve a intentar.

     El mensaje no repite el rótulo del botón ("Imagen copiada"): el botón ya
     dice QUÉ pasó, así que la línea dice QUÉ HACER con eso. Cada elemento,
     un solo trabajo. */
  const confirmar = useCallback((cual, mensaje) => {
    clearTimeout(reloj.current);
    setLogro(cual);
    setEstado(mensaje);
    setTono('ok');
    reloj.current = setTimeout(() => {
      setLogro(null);
      setEstado('');
      setTono('');
    }, MS_CONFIRMACION);
  }, []);

  const fallar = useCallback((mensaje) => {
    clearTimeout(reloj.current);
    setLogro(null);
    setEstado(mensaje);
    setTono('error');
  }, []);

  /* ── Teléfono: la hoja del sistema, con la imagen adentro ─────────────── */
  const alCompartirNativo = useCallback(async () => {
    setEstado('');
    setTono('');
    setTrabajando(true);

    let archivo = null;
    try {
      archivo = await preparar();
    } catch (e) {
      archivo = null;
    }

    try {
      if (ruta === 'imagen-nativa' && archivo) {
        /* La IMAGEN SOLA. Ya lleva lotería, turno, sorteo, fecha, los números,
           la atribución y el dominio; en la jugada, la banda "JUGADA SIMULADA"
           y la marca de agua. Mandar el texto al lado repetía todo eso en el
           único formato que se puede editar antes de reenviar, y encima
           ocupaba la vista previa del chat.
           En un extracto va además el link: es corto, deja verificarlo en el
           sitio y WhatsApp le arma vista previa. */
        const carga = { files: [archivo] };
        if (
          enlace &&
          (typeof navigator.canShare !== 'function' ||
            navigator.canShare({ files: [archivo], url: enlace }))
        ) {
          carga.url = enlace;
        }
        try {
          await navigator.share(carga);
          return;
        } catch (e) {
          if (e && e.name === 'AbortError') return;
          // cualquier otro fallo sigue por el camino de abajo
        }
      }

      // Respaldo: este navegador no manda archivos. Recién acá aparece el
      // texto, que es lo único que queda.
      try {
        await navigator.share({ title: titulo, text: texto, url: enlace || url });
        if (archivo) fallar('Este navegador no manda imágenes: se compartió el texto.');
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }

      // Último respaldo del teléfono, igual que siempre: WhatsApp con el
      // texto. En el celular ese link SÍ abre la app.
      const abrio = !!window.open(
        enlaceWhatsApp(texto, enlace || url), '_blank', 'noopener,noreferrer');
      if (!abrio) {
        fallar('El navegador bloqueó la ventana de WhatsApp. Habilitá las ventanas emergentes y probá de nuevo.');
      } else if (archivo) {
        fallar('Este navegador no manda imágenes: se abrió WhatsApp con el texto.');
      }
    } finally {
      setTrabajando(false);
    }
  }, [ruta, preparar, titulo, texto, url, enlace, fallar]);

  /* ── Escritorio: copiar la imagen ─────────────────────────────────────── */
  const alCopiarImagen = useCallback(async () => {
    limpiar();
    setTrabajando(true);
    const promesa = preparar(); // sin await: el gesto tiene que seguir vivo

    if (!puedeCopiar) {
      const archivo = await promesa;
      setTrabajando(false);
      if (!archivo) {
        fallar('No se pudo armar la imagen. Probá de nuevo.');
        return;
      }
      descargar(archivo);
      confirmar('imagen-descargada', 'Listo: quedó en tu carpeta de descargas.');
      return;
    }

    const r = await copiarImagen(promesa);
    setTrabajando(false);

    if (r === 'ok') {
      confirmar('imagen-copiada', 'Listo: pegala con Ctrl+V donde quieras.');
      return;
    }
    if (r === 'sin-imagen') {
      fallar('No se pudo armar la imagen. Probá de nuevo.');
      return;
    }
    setOfrecerDescarga(true);
    fallar('El navegador no dejó copiar la imagen. Descargala y mandala como archivo.');
  }, [limpiar, preparar, puedeCopiar, confirmar, fallar]);

  const alDescargar = useCallback(async () => {
    const archivo = await preparar();
    if (!archivo) {
      fallar('No se pudo armar la imagen. Probá de nuevo.');
      return;
    }
    descargar(archivo);
    setOfrecerDescarga(false);
    confirmar('imagen-descargada', 'Listo: quedó en tu carpeta de descargas.');
  }, [preparar, confirmar, fallar]);

  /* ── Escritorio: ENVIAR el link por WhatsApp ──────────────────────────
     Acá sí se nombra a WhatsApp, y no es una promesa vacía: `wa.me` no puede
     mandar archivos, pero un link es texto y eso viaja perfecto. El link
     además abre el extracto en el sitio, así que lo que llega es verificable
     —y no se puede editar como se editaría una lista de números pegada.

     Se manda un resumen corto, NO los 20 números: mandarlos como texto sería
     volver a lo que se puede adulterar, que es justo lo que la imagen vino a
     resolver. El dato completo está del otro lado del link. */
  const alEnviarPorWhatsApp = useCallback(() => {
    limpiar();
    const cuerpo = resumen || titulo || '';
    const ventana = window.open(
      enlaceWhatsApp(cuerpo, enlace), '_blank', 'noopener,noreferrer');
    if (ventana) {
      confirmar('link-enviado', 'Se abrió WhatsApp con el link a este extracto.');
      return;
    }
    // Bloqueador de ventanas emergentes: se muestra el link para copiarlo.
    setLinkALaVista(true);
    fallar('El navegador bloqueó la ventana. Copiá el link y mandalo a mano:');
  }, [limpiar, resumen, titulo, enlace, confirmar, fallar]);

  useEffect(() => {
    if (!linkALaVista) return;
    campoLink.current?.select();
  }, [linkALaVista]);

  /* ── Lo que se dibuja ─────────────────────────────────────────────────── */

  if (ruta !== 'escritorio') {
    /* Teléfono. Acá el rótulo no nombra a WhatsApp: se abre la hoja del
       sistema, donde WhatsApp es una opción entre otras. */
    return (
      <span className={`cp cp-${variante}`}>
        <button
          type="button"
          className="cp-btn cp-btn-principal"
          onClick={alCompartirNativo}
          onPointerDown={preparar}
          onFocus={preparar}
          aria-label={aria}
          tabIndex={tabIndex}
          aria-busy={trabajando || undefined}
        >
          <IconoCompartir />
          <span>{etiqueta || 'Compartir'}</span>
        </button>
        {estado && <span className="cp-estado" role="status">{estado}</span>}
      </span>
    );
  }

  const copiadaImagen = logro === 'imagen-copiada' || logro === 'imagen-descargada';
  const rotuloImagen = logro === 'imagen-copiada'
    ? 'Imagen copiada'
    : logro === 'imagen-descargada'
      ? 'Imagen descargada'
      : puedeCopiar ? 'Copiar la imagen' : 'Descargar la imagen';

  return (
    <span className={`cp cp-esc cp-${variante}`}>
      <span className="cp-fila">
        <button
          type="button"
          className="cp-btn cp-btn-principal"
          data-hecho={copiadaImagen || undefined}
          onClick={alCopiarImagen}
          onPointerDown={preparar}
          onFocus={preparar}
          aria-label={copiadaImagen ? undefined : (ariaCopia || aria)}
          tabIndex={tabIndex}
          aria-busy={trabajando || undefined}
        >
          {copiadaImagen ? <IconoHecho /> : puedeCopiar ? <IconoCopiarImagen /> : <IconoDescargar />}
          <span>{rotuloImagen}</span>
        </button>

        {enlace && (
          <button
            type="button"
            className="cp-btn cp-btn-suave"
            data-hecho={logro === 'link-enviado' || undefined}
            onClick={alEnviarPorWhatsApp}
            aria-label={logro === 'link-enviado' ? undefined : ariaEnlace}
            tabIndex={tabIndex}
          >
            {logro === 'link-enviado' ? <IconoHecho /> : <IconoWhatsApp />}
            <span>{logro === 'link-enviado' ? 'WhatsApp abierto' : 'Enviar por WhatsApp'}</span>
          </button>
        )}

        {ofrecerDescarga && (
          <button
            type="button"
            className="cp-btn cp-btn-suave"
            onClick={alDescargar}
            tabIndex={tabIndex}
          >
            <IconoDescargar />
            <span>Descargar la imagen</span>
          </button>
        )}
      </span>

      {estado && (
        <span className={`cp-estado${tono ? ` cp-${tono}` : ''}`} role="status">
          {estado}
        </span>
      )}

      {linkALaVista && (
        <input
          className="cp-link"
          ref={campoLink}
          type="text"
          readOnly
          value={enlace}
          aria-label="Link a este extracto"
          onFocus={(e) => e.target.select()}
        />
      )}
    </span>
  );
}
